import { computed, onMounted, ref, watch, watchEffect } from 'vue';
import { useAccountStore } from 'src/stores/account';
import { useConvoStore } from 'src/stores/conversations';
import useQuasar from 'quasar/src/composables/use-quasar/use-quasar.js';
import { useRouter } from 'vue-router';
import cloneDeep from 'lodash.clonedeep';
/**
 * Plugin containing the checker if
 * there are messages sending in progress
 *
 * @param backFunctionCb function to call when close type is back
 * @param closeFunctionCb function to call when close type is close
 */
const ChatCheckSendingPlugin = (backFunctionCb, closeFunctionCb) => {
    const $q = useQuasar();
    const vueRouter = useRouter();
    const accountStore = useAccountStore();
    const convoStore = useConvoStore();
    const sending = computed(() => {
        return convoStore.sending;
    });
    const uploading = computed(() => {
        return convoStore.uploading;
    });
    const user = computed(() => accountStore.user);
    const savedPath = computed({
        get: () => {
            return convoStore.enroute;
        },
        set: (newValue) => {
            void convoStore.setRoute(newValue);
        }
    });
    const closeTab = ref(false);
    const toClose = ref(false);
    const closeType = ref(null);
    /**
     * Checks if there are any messages sending in progress
     * before exiting the popup
     *
     * @param close if need to close after sending is successful.
     * @param type type of closure for custom emit
     */
    function checkSend(close = true, type = 'back') {
        if (!uploading.value && !sending.value)
            return true;
        if (close) {
            toClose.value = true;
            closeType.value = type;
        }
        $q.loading.show({
            message: 'Finishing up',
            spinnerSize: 250,
            spinnerColor: 'accent'
        });
        return false;
    }
    watchEffect(() => {
        if (sending.value || uploading.value)
            return;
        if (toClose.value) {
            /**
             * If place is awaiting closure,
             * call functions to close the chat
             */
            if (closeType.value === 'back') {
                backFunctionCb();
            }
            else {
                closeFunctionCb();
            }
            toClose.value = false;
            closeType.value = '';
        }
        else if (closeTab.value &&
            (typeof window !== 'undefined')) {
            // for app type
            window.close();
        }
        else if (savedPath.value) {
            /**
             * Reroute to saved path
             */
            const savedPathCopy = cloneDeep(savedPath.value);
            savedPath.value = null;
            void vueRouter.push({ ...savedPathCopy });
        }
    });
    watch(savedPath, (newValue) => {
        /**
         * For new saved paths,
         * check if can redirect
         */
        if (!newValue)
            return;
        checkSend(false);
    });
    onMounted(() => {
        /**
         * Prevent window unload if there is still msgs sending
         */
        window?.addEventListener('beforeunload', event => {
            const closable = checkSend(false);
            closeTab.value = true;
            if (!closable) {
                event.preventDefault();
                event.returnValue = 'Messages will not be sent if you close the window';
            }
            else {
                void convoStore.clearConversation('chat', user.value?.user_pk.toString() || '');
            }
        });
    });
    return {
        closeTab,
        savedPath,
        checkSend
    };
};
export { ChatCheckSendingPlugin };
